import { useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Clock,
  ImageOff,
  MapPin,
  Search,
  Star,
  UtensilsCrossed,
} from 'lucide-react';
import { getErrorMessage } from '@/lib/api';
import { cn, formatCOP, formatDistance } from '@/lib/cn';
import { useComercio, type ProductoPublico } from '@/lib/queries';
import { PedidoPorLista } from '@/components/PedidoPorLista';
import { puntoDeBusqueda, useUbicacion } from '@/lib/ubicacion';
import { Cargando, Entrada, ErrorCarga, Vacio } from '@/components/ui';
import { ModalProducto } from '@/components/ModalProducto';

export function Comercio() {
  const { slug = '' } = useParams();
  const ubicacion = useUbicacion((s) => s.ubicacion);
  const punto = puntoDeBusqueda(ubicacion);

  const comercio = useComercio(slug, punto);
  const [filtro, setFiltro] = useState('');
  const [elegido, setElegido] = useState<ProductoPublico | null>(null);
  const seccionesRef = useRef<Record<string, HTMLElement | null>>({});

  /** Menú filtrado por el buscador interno; oculta las secciones que quedan vacías. */
  const menu = useMemo(() => {
    if (!comercio.data) return [];
    const termino = filtro.trim().toLowerCase();

    return comercio.data.menu
      .map((seccion) => ({
        ...seccion,
        products: termino
          ? seccion.products.filter(
              (p) =>
                p.name.toLowerCase().includes(termino) ||
                p.description?.toLowerCase().includes(termino),
            )
          : seccion.products,
      }))
      .filter((seccion) => seccion.products.length > 0);
  }, [comercio.data, filtro]);

  if (comercio.isLoading) return <Cargando texto="Cargando el menú…" />;

  if (comercio.isError) {
    return (
      <div className="mx-auto max-w-md pt-8">
        <ErrorCarga
          mensaje={getErrorMessage(comercio.error)}
          onReintentar={() => comercio.refetch()}
        />
        <Link to="/" className="mt-4 block text-center text-kick-red hover:underline">
          Volver a los comercios
        </Link>
      </div>
    );
  }

  const datos = comercio.data!;

  // Pedido por lista: sin catálogo que mostrar, la pantalla es otra.
  if (datos.listOrdering) return <PedidoPorLista comercio={datos} />;
  const horarioHoy = datos.schedules.find((h) => h.weekday === new Date().getDay());

  return (
    <div className="pb-24 sm:pb-8">
      <Link
        to="/"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-kick-gray transition hover:text-kick-ink"
      >
        <ArrowLeft className="h-4 w-4" />
        Comercios
      </Link>

      {/* Cabecera */}
      <header className="overflow-hidden rounded-kick bg-white shadow-card">
        <div className="relative h-40 bg-kick-light sm:h-56">
          {datos.coverUrl ? (
            <img
              src={datos.coverUrl}
              alt=""
              className={cn('h-full w-full object-cover', !datos.isOpen && 'grayscale')}
            />
          ) : (
            <div className="grid h-full place-items-center text-kick-gray/30">
              <UtensilsCrossed className="h-10 w-10" />
            </div>
          )}

          {!datos.isOpen && (
            <div className="absolute inset-0 grid place-items-center bg-black/50">
              <div className="rounded-kick bg-white px-4 py-2 text-center">
                <p className="font-bold text-kick-dark">Cerrado ahora</p>
                {horarioHoy && !horarioHoy.isClosed && (
                  <p className="text-sm text-kick-gray">
                    Hoy atiende de {horarioHoy.opensAt.slice(0, 5)} a{' '}
                    {horarioHoy.closesAt.slice(0, 5)}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-4 p-4">
          {datos.logoUrl && (
            /**
             * `relative` no es decorativo: sin él, el logo queda tapado.
             *
             * El bloque de la portada es `relative` —lo necesita para el velo
             * de «Cerrado ahora»— y un elemento posicionado se pinta siempre
             * por encima de uno estático, aunque venga antes en el documento.
             * Como el logo sube con `-mt-12` para montarse sobre la portada,
             * terminaba debajo de ella y solo se veía su mitad inferior.
             */
            <img
              src={datos.logoUrl}
              alt=""
              className="relative z-10 -mt-12 h-20 w-20 shrink-0 rounded-kick border-4 border-white bg-white object-cover shadow"
            />
          )}

          <div className="min-w-0 flex-1">
            <h1 className="text-2xl text-kick-dark">{datos.name}</h1>
            <p className="text-sm text-kick-gray">{datos.categoryName}</p>

            {datos.description && (
              <p className="mt-2 text-sm text-kick-ink">{datos.description}</p>
            )}

            <dl className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-kick-gray">
              {datos.ratingCount > 0 && (
                <div className="flex items-center gap-1">
                  <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                  <span className="font-semibold text-kick-ink">
                    {datos.ratingAvg.toFixed(1)}
                  </span>
                  <span>({datos.ratingCount})</span>
                </div>
              )}
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {datos.avgPrepMinutes} min
              </div>
              {datos.distanceMeters !== undefined && (
                <div className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {formatDistance(datos.distanceMeters)}
                </div>
              )}
              {datos.minOrderAmount > 0 && (
                <div>Pedido mínimo {formatCOP(datos.minOrderAmount)}</div>
              )}
            </dl>
          </div>
        </div>
      </header>

      {/* Navegación del menú, pegajosa al desplazar */}
      {menu.length > 0 && (
        <div className="sticky top-[68px] z-20 -mx-4 mt-4 bg-kick-light/95 px-4 py-3 backdrop-blur sm:mx-0 sm:px-0">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <nav aria-label="Secciones del menú" className="min-w-0 flex-1">
              <ul className="flex gap-2 overflow-x-auto pb-1">
                {menu.map((seccion) => (
                  <li key={seccion.id}>
                    <button
                      onClick={() =>
                        seccionesRef.current[seccion.id]?.scrollIntoView({
                          behavior: 'smooth',
                          block: 'start',
                        })
                      }
                      className="whitespace-nowrap rounded-full bg-white px-3.5 py-1.5 text-sm font-semibold text-kick-ink shadow-card transition hover:bg-kick-red hover:text-white"
                    >
                      {seccion.name}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="relative sm:w-56">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-kick-gray" />
              <Entrada
                type="search"
                value={filtro}
                onChange={(e) => setFiltro(e.target.value)}
                placeholder="Buscar en el menú"
                aria-label="Buscar en el menú"
                className="py-2 pl-9 text-sm"
              />
            </div>
          </div>
        </div>
      )}

      {/* Menú */}
      {menu.length === 0 ? (
        <div className="mt-6">
          <Vacio
            icono={<UtensilsCrossed className="h-6 w-6" />}
            titulo={filtro ? 'Sin resultados' : 'Este comercio aún no publica su menú'}
            descripcion={
              filtro
                ? `No encontramos nada con «${filtro}». Prueba con otra palabra.`
                : 'Vuelve más tarde.'
            }
          />
        </div>
      ) : (
        <div className="mt-4 space-y-8">
          {menu.map((seccion) => (
            <section
              key={seccion.id}
              ref={(el) => {
                seccionesRef.current[seccion.id] = el;
              }}
              className="scroll-mt-32"
            >
              <h2 className="mb-3 text-xl text-kick-dark">{seccion.name}</h2>

              <ul className="grid gap-3 sm:grid-cols-2">
                {seccion.products.map((producto) => (
                  <li key={producto.id}>
                    <TarjetaProducto
                      producto={producto}
                      comercioAbierto={datos.isOpen}
                      onElegir={() => setElegido(producto)}
                    />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      {elegido && (
        <ModalProducto
          abierto
          producto={elegido}
          comercio={{
            id: datos.id,
            slug: datos.slug,
            name: datos.name,
            logoUrl: datos.logoUrl,
            minOrderAmount: datos.minOrderAmount,
            avgPrepMinutes: datos.avgPrepMinutes,
            location: datos.location,
          }}
          onCerrar={() => setElegido(null)}
        />
      )}
    </div>
  );
}

function TarjetaProducto({
  producto,
  comercioAbierto,
  onElegir,
}: {
  producto: ProductoPublico;
  comercioAbierto: boolean;
  onElegir: () => void;
}) {
  const disponible = producto.isAvailable && comercioAbierto;

  return (
    <button
      onClick={onElegir}
      disabled={!disponible}
      className={cn(
        'card-kick flex w-full gap-3 p-3 text-left',
        !disponible && 'cursor-not-allowed opacity-60 hover:shadow-card',
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-2">
          <h3 className="font-semibold text-kick-ink">{producto.name}</h3>
          {!producto.isAvailable && (
            <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
              Agotado
            </span>
          )}
        </div>

        {producto.description && (
          <p className="mt-0.5 line-clamp-2 text-sm text-kick-gray">{producto.description}</p>
        )}

        <div className="mt-2 flex items-baseline gap-2">
          <span className="font-bold text-kick-dark">{formatCOP(producto.price)}</span>
          {producto.compareAtPrice && (
            <span className="text-sm text-kick-gray line-through">
              {formatCOP(producto.compareAtPrice)}
            </span>
          )}
        </div>

        {producto.optionGroups.length > 0 && (
          <p className="mt-1 text-xs text-kick-gray">
            {producto.optionGroups.length} opción
            {producto.optionGroups.length === 1 ? '' : 'es'} para elegir
          </p>
        )}
      </div>

      {producto.imageUrl ? (
        <img
          src={producto.imageUrl}
          alt=""
          loading="lazy"
          className={cn(
            'h-24 w-24 shrink-0 rounded-kick bg-kick-light object-contain',
            !disponible && 'grayscale',
          )}
        />
      ) : (
        <div className="grid h-24 w-24 shrink-0 place-items-center rounded-kick bg-kick-light text-kick-gray/40">
          <ImageOff className="h-6 w-6" />
        </div>
      )}
    </button>
  );
}
